import { projectZql } from "./projects-schema.js";
function writableProjectQuery(ctx) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.projects;
    }
    const personID = ctx.personID;
    if (ctx.role === "freelancer" && personID) {
        return projectZql.projects.whereExists("collaborators", (collaborator) => collaborator.where((eb) => eb.and(eb.cmp("personID", personID), eb.cmp("canEdit", true))));
    }
    return projectZql.projects.where("id", "__unauthorized__");
}
export function toZeroDate(value) {
    if (value === null)
        return null;
    if (value === undefined)
        return undefined;
    const [year, month, day] = value.split("-").map(Number);
    return Date.UTC(year, month - 1, day);
}
export function toZeroDateArray(values) {
    return (values ?? [])
        .map((value) => toZeroDate(value))
        .filter((value) => typeof value === "number");
}
export function normalizeZeroDateArray(values) {
    return [...new Set(toZeroDateArray(values))].sort((a, b) => a - b);
}
export async function syncProjectFunctionSeriesDays(tx, args) {
    const selectedDays = [...new Set(args.selectedDays)].sort((a, b) => a - b);
    const existingDays = await tx.run(projectZql.projectFunctions.where("seriesID", args.seriesID));
    const existingByDate = new Map((existingDays ?? []).map((day) => [
        day.functionDate,
        day,
    ]));
    const selectedDaySet = new Set(selectedDays);
    for (const day of existingDays ?? []) {
        if (!selectedDaySet.has(day.functionDate)) {
            await tx.mutate.projectFunctions.delete({ id: day.id });
            continue;
        }
        await tx.mutate.projectFunctions.update({
            id: day.id,
            name: args.name,
            requiredCount: args.requiredCount,
        });
    }
    for (const functionDate of selectedDays) {
        if (existingByDate.has(functionDate))
            continue;
        await tx.mutate.projectFunctions.insert({
            id: args.dayIDsByDate?.[functionDate] ?? crypto.randomUUID(),
            projectID: args.projectID,
            seriesID: args.seriesID,
            functionDate,
            name: args.name,
            requiredCount: args.requiredCount,
        });
    }
}
export function normalizeProjectInput(args) {
    return {
        ...args,
        startDate: toZeroDate(args.startDate),
        endDate: toZeroDate(args.endDate),
        eventStartDate: toZeroDate(args.eventStartDate),
        eventEndDate: toZeroDate(args.eventEndDate),
        planningStartDate: toZeroDate(args.planningStartDate),
        planningEndDate: toZeroDate(args.planningEndDate),
    };
}
export async function assertProjectWriteAccess(tx, ctx, projectID) {
    const project = await tx.run(writableProjectQuery(ctx).where("id", projectID).one());
    if (!project) {
        throw new Error("Not allowed to edit this project");
    }
}
/** Quotes and invoices are employee-only (reads and writes), even for
 *  freelancers who may edit the project itself. */
export function assertFinanceAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Finance is limited to employees");
    }
}
/** Project write access plus the employee-only finance rule. */
export async function assertProjectFinanceWriteAccess(tx, ctx, projectID) {
    assertFinanceAccess(ctx);
    await assertProjectWriteAccess(tx, ctx, projectID);
}
export async function assertQuoteVersionWriteAccess(tx, ctx, quoteVersionID) {
    assertFinanceAccess(ctx);
    const version = await tx.run(projectZql.quoteVersions.where("id", quoteVersionID).one());
    if (!version)
        throw new Error("Quote version not found");
    const quote = await tx.run(projectZql.quotes.where("id", version.quoteID).one());
    if (!quote)
        throw new Error("Quote not found");
    await assertProjectWriteAccess(tx, ctx, quote.projectID);
    return { version, quote };
}
export async function assertQuoteGroupWriteAccess(tx, ctx, quoteGroupID) {
    assertFinanceAccess(ctx);
    const group = await tx.run(projectZql.quoteGroups.where("id", quoteGroupID).one());
    if (!group)
        throw new Error("Quote group not found");
    await assertQuoteVersionWriteAccess(tx, ctx, group.quoteVersionID);
    return group;
}
export async function assertQuoteLineItemWriteAccess(tx, ctx, quoteLineItemID) {
    assertFinanceAccess(ctx);
    const item = await tx.run(projectZql.quoteLineItems.where("id", quoteLineItemID).one());
    if (!item)
        throw new Error("Quote line item not found");
    await assertQuoteGroupWriteAccess(tx, ctx, item.quoteGroupID);
    return item;
}
export async function assertQuoteDeductionItemWriteAccess(tx, ctx, quoteDeductionItemID) {
    assertFinanceAccess(ctx);
    const item = await tx.run(projectZql.quoteDeductionItems.where("id", quoteDeductionItemID).one());
    if (!item)
        throw new Error("Quote deduction item not found");
    await assertQuoteVersionWriteAccess(tx, ctx, item.quoteVersionID);
    return item;
}
export async function assertInvoiceWriteAccess(tx, ctx, invoiceID) {
    assertFinanceAccess(ctx);
    const invoice = await tx.run(projectZql.invoices.where("id", invoiceID).one());
    if (!invoice)
        throw new Error("Invoice not found");
    if (!invoice.projectID) {
        if (ctx.role !== "admin" && ctx.role !== "employee") {
            throw new Error("Not allowed to edit standalone invoices");
        }
        return invoice;
    }
    await assertProjectWriteAccess(tx, ctx, invoice.projectID);
    return invoice;
}
export async function assertInvoiceGroupWriteAccess(tx, ctx, invoiceGroupID) {
    assertFinanceAccess(ctx);
    const group = await tx.run(projectZql.invoiceGroups.where("id", invoiceGroupID).one());
    if (!group)
        throw new Error("Invoice group not found");
    await assertInvoiceWriteAccess(tx, ctx, group.invoiceID);
    return group;
}
export async function assertInvoiceLineItemWriteAccess(tx, ctx, invoiceLineItemID) {
    assertFinanceAccess(ctx);
    const item = await tx.run(projectZql.invoiceLineItems.where("id", invoiceLineItemID).one());
    if (!item)
        throw new Error("Invoice line item not found");
    await assertInvoiceGroupWriteAccess(tx, ctx, item.invoiceGroupID);
    return item;
}
export async function assertInvoiceDeductionItemWriteAccess(tx, ctx, invoiceDeductionItemID) {
    assertFinanceAccess(ctx);
    const item = await tx.run(projectZql.invoiceDeductionItems.where("id", invoiceDeductionItemID).one());
    if (!item)
        throw new Error("Invoice deduction item not found");
    await assertInvoiceWriteAccess(tx, ctx, item.invoiceID);
    return item;
}
export function assertProjectCreateAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to create projects");
    }
}
export function assertCompanyWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to edit companies");
    }
}
export function assertProjectGroupWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to edit project groups");
    }
}
export function assertPersonWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to edit people");
    }
}
export function assertPersonAdminFieldWriteAccess(ctx) {
    if (ctx.role !== "admin") {
        throw new Error("Only admins can change admin status");
    }
}
export function assertSkillWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to edit skill data");
    }
}
export function assertTaskWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to edit tasks");
    }
}
export function assertCommentWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to edit comments");
    }
}
export function assertMeetingNoteWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to edit meeting notes");
    }
}
/** Managers-only meeting notes and documents are visible to admins only. */
export function canSeeAdminOnlyContent(ctx) {
    return ctx.role === "admin";
}
export function assertAdminOnlyFlagWriteAccess(ctx) {
    if (!canSeeAdminOnlyContent(ctx)) {
        throw new Error("Only managers can change managers-only visibility");
    }
}
export function assertAdminOnlyRowAccess(ctx, row) {
    if (row?.adminOnly && !canSeeAdminOnlyContent(ctx)) {
        throw new Error("Not allowed to access managers-only content");
    }
}
export function assertFinanceTemplateWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to edit finance templates");
    }
}
export function assertAdminDataWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to edit admin data");
    }
}
export const emptyStudioConfigData = {
    layouts: [],
    profiles: [],
};
export async function assertStreamWriteAccess(tx, ctx, streamID) {
    const stream = await tx.run(projectZql.streams.where("id", streamID).one());
    if (!stream)
        throw new Error("Stream not found");
    await assertProjectWriteAccess(tx, ctx, stream.projectID);
    return stream;
}
export async function assertStreamTakerWriteAccess(tx, ctx, streamTakerID) {
    const taker = await tx.run(projectZql.streamTakers.where("id", streamTakerID).one());
    if (!taker)
        throw new Error("Stream taker not found");
    await assertProjectWriteAccess(tx, ctx, taker.projectID);
    return taker;
}
export async function replacePersonAllergyTags(tx, personID, tagIDs) {
    const existing = await tx.run(projectZql.personAllergyTags.where("personID", personID));
    for (const assignment of existing ?? []) {
        await tx.mutate.personAllergyTags.delete({ id: assignment.id });
    }
    for (const allergyTagID of tagIDs) {
        await tx.mutate.personAllergyTags.insert({
            id: crypto.randomUUID(),
            personID,
            allergyTagID,
        });
    }
}
export async function replaceMeetingNoteTagAssignments(tx, meetingNoteID, tagIDs) {
    const existing = await tx.run(projectZql.meetingNoteTagAssignments.where("meetingNoteID", meetingNoteID));
    for (const assignment of existing ?? []) {
        await tx.mutate.meetingNoteTagAssignments.delete({ id: assignment.id });
    }
    for (const tagID of tagIDs) {
        await tx.mutate.meetingNoteTagAssignments.insert({
            id: crypto.randomUUID(),
            meetingNoteID,
            tagID,
        });
    }
}
export async function replaceMeetingNoteMentions(tx, meetingNoteID, personIDs) {
    const existing = await tx.run(projectZql.meetingNoteMentions.where("meetingNoteID", meetingNoteID));
    for (const mention of existing ?? []) {
        await tx.mutate.meetingNoteMentions.delete({ id: mention.id });
    }
    for (const personID of personIDs) {
        await tx.mutate.meetingNoteMentions.insert({
            id: crypto.randomUUID(),
            meetingNoteID,
            personID,
        });
    }
}
export async function replaceQuoteGroupTemplateItems(tx, groupTemplateID, items) {
    const existing = await tx.run(projectZql.quoteGroupTemplateItems.where("groupTemplateID", groupTemplateID));
    for (const item of existing ?? []) {
        await tx.mutate.quoteGroupTemplateItems.delete({ id: item.id });
    }
    const now = Date.now();
    for (const [index, item] of items.entries()) {
        await tx.mutate.quoteGroupTemplateItems.insert({
            id: crypto.randomUUID(),
            groupTemplateID,
            lineItemTemplateID: item.isTitle ? null : item.lineItemTemplateID,
            defaultAmount: item.defaultAmount ?? (item.isTitle ? 0 : 1),
            sortOrder: item.sortOrder ?? index,
            isTitle: item.isTitle ?? false,
            name: item.isTitle ? (item.name ?? {}) : null,
            createdAt: now,
            updatedAt: now,
        });
    }
}
export function queueClockifyProjectSync(ctx, projectID) {
    ctx.clockifyProjectSyncQueue?.add(projectID);
}
export function queueClockifyProjectGroupSync(ctx, projectGroupID) {
    ctx.clockifyProjectGroupSyncQueue?.add(projectGroupID);
}
